import { type BaseEntity } from "@/shared/types";
import { z } from "zod";
import { phoneSchema } from "@/shared/lib/utils/validation";
import type { BarcodeSize } from "@entities/part";

export interface Branchcategory extends BaseEntity {
  tenant_id: string;
  name: string;
  color?: string;
}

export interface Branch extends BaseEntity {
  tenant_id: string;
  name: string;
  branchcategory_id?: string;
  street?: string;
  zip?: string;
  city?: string;
  country?: string;
  phone?: string;
  is_template?: boolean;
  latitude?: number;
  longitude?: number;
  /** Resolved category object (from GraphQL when requested) */
  category?: { id: string; name: string; color?: string };
  /** Number of assets at this location (from GraphQL when requested) */
  assetCount?: number;
  /** Number of users assigned to this location (from GraphQL when requested) */
  userCount?: number;
}

export const branchcategoryFormSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, "common:validation.nameRequired")
    .max(100, "common:validation.nameMaxLength100"),
  color: z.string().optional().or(z.literal("")),
});

export type BranchcategoryFormData = z.infer<typeof branchcategoryFormSchema>;

export const branchFormSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, "common:validation.nameRequired")
    .max(200, "common:validation.nameMaxLength200"),
  branchcategory_id: z.string().optional().or(z.literal("")),
  street: z.string().max(200).optional().or(z.literal("")),
  zip: z.string().max(20).optional().or(z.literal("")),
  city: z.string().max(100).optional().or(z.literal("")),
  country: z.string().optional().or(z.literal("")),
  phone: phoneSchema,
  is_template: z.boolean().optional(),
  latitude: z.number().optional(),
  longitude: z.number().optional(),
});

export type BranchFormData = z.infer<typeof branchFormSchema>;

/**
 * Label configuration types for location (branch) QR code label generation
 */

export type LocationLabelElementType = "qr" | "text" | "barcode";

export type LocationLabelPaperFormat = "A4" | "62x69" | "76x25" | "76x52";

export type LocationLabelQRSize = "small" | "standard" | "large";

export type LocationLabelTextType = "id" | "name" | "address";

export interface LocationLabelElementPosition {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface LocationLabelElementBase {
  id: string;
  type: LocationLabelElementType;
  position: LocationLabelElementPosition;
  visible: boolean;
}

export interface LocationLabelQRCodeElement extends LocationLabelElementBase {
  type: "qr";
  size: LocationLabelQRSize;
}

export interface LocationLabelTextElement extends LocationLabelElementBase {
  type: "text";
  textType: LocationLabelTextType;
  fontSize: number;
  fontWeight: "normal" | "bold";
}

export interface LocationLabelBarcodeElement extends LocationLabelElementBase {
  type: "barcode";
  size: BarcodeSize;
}

export type AnyLocationLabelElement =
  | LocationLabelQRCodeElement
  | LocationLabelTextElement
  | LocationLabelBarcodeElement;

export interface LocationLabelPaperFormatSpec {
  name: string;
  widthMm: number;
  heightMm: number;
  widthPx: number;
  heightPx: number;
}

export const LOCATION_LABEL_PAPER_FORMATS: Record<
  LocationLabelPaperFormat,
  LocationLabelPaperFormatSpec
> = {
  A4: {
    name: "A4",
    widthMm: 210,
    heightMm: 297,
    widthPx: 794,
    heightPx: 1123,
  },
  "62x69": {
    name: "62 × 69 mm",
    widthMm: 62,
    heightMm: 69,
    widthPx: 234,
    heightPx: 261,
  },
  "76x25": {
    name: "76 × 25 mm",
    widthMm: 76,
    heightMm: 25,
    widthPx: 287,
    heightPx: 94,
  },
  "76x52": {
    name: "76 × 52 mm",
    widthMm: 76,
    heightMm: 52,
    widthPx: 287,
    heightPx: 196,
  },
};

export const LOCATION_LABEL_QR_SIZES_PX: Record<LocationLabelQRSize, number> = {
  small: 80,
  standard: 120,
  large: 150,
};

export interface LocationLabelConfiguration {
  paperFormat: LocationLabelPaperFormat;
  qrSize: LocationLabelQRSize;
  barcodeSize: BarcodeSize;
  elements: AnyLocationLabelElement[];
}

export const DEFAULT_LOCATION_LABEL_CONFIG: LocationLabelConfiguration = {
  paperFormat: "A4",
  qrSize: "standard",
  barcodeSize: "standard",
  elements: [
    {
      id: "qr-1",
      type: "qr",
      size: "standard",
      position: { x: 50, y: 50, width: 120, height: 120 },
      visible: true,
    },
    {
      id: "text-name",
      type: "text",
      textType: "name",
      fontSize: 18,
      fontWeight: "bold",
      position: { x: 190, y: 50, width: 300, height: 30 },
      visible: true,
    },
    {
      id: "text-address",
      type: "text",
      textType: "address",
      fontSize: 14,
      fontWeight: "normal",
      position: { x: 190, y: 90, width: 300, height: 40 },
      visible: true,
    },
    {
      id: "text-id",
      type: "text",
      textType: "id",
      fontSize: 12,
      fontWeight: "normal",
      position: { x: 190, y: 140, width: 300, height: 20 },
      visible: true,
    },
  ],
};

export type { BarcodeSize };
